import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import PageBlock from 'app/shared/componentsRestyled/common/PageBlock';
import { TableCDS } from 'app/shared/componentsRestyled/coreDesignSystem/Table';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { compose } from 'redux';

import useLoanId from 'hooks/useLoanId';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';
import usePopover from 'hooks/usePopover';

import {
  generateNewUwReport,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
  getUwReportsByLoanIdAction,
} from 'store/loanApplications/actions';
import { selectIsLoanCanceled, selectUwReportsList } from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import ActionMenu from './ActionMenu';
import { config, getItemRow } from './UwReports.config';

const UwReports = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();
  const loanId = useLoanId();

  const fetchData = useCallback(() => {
    if (!loanId) {
      return;
    }

    setIsLoading(true);

    dispatch(getUwReportsByLoanIdAction({ loanId }))
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, loanId, setIsLoading]);

  usePendingChangesDependentFetch(fetchData);

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const reports = useSelector(selectUwReportsList);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);

  const data = useMemo(() => {
    if (!reports) {
      return [];
    }

    const sortedReports = _.orderBy(reports, 'createdDate', 'desc');

    return sortedReports.map(getItemRow);
  }, [reports]);

  const onReportGenerate = useCallback(() => {
    setIsLoading(true);

    dispatch(generateNewUwReport({ loanId }))
      .then(() => dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId })))
      .then(() => dispatch(getLoanApplicationHeaderViewAction({ applicationId })))
      .finally(() => setIsLoading(false));
  }, [setIsLoading, dispatch, loanId, applicationId]);

  return (
    <PageBlock
      withAction
      withDivider
      isActionDisabled={isLoading}
      isMenuOpened={!!anchorEl}
      title="UW Reports"
      onMenuClick={onSetAnchorElement}
    >
      <TableCDS
        isCustomDataDefinition
        withNoAvailableMessage
        config={config}
        data={data}
        data-testid={UnderwritingTestIds.UW_REPORTS_TABLE}
        isLoading={isLoading}
        withDivider={false}
      />

      <ActionMenu
        anchorEl={anchorEl}
        isGenerateReportDisabled={isLoanCanceled}
        onClearAnchorElement={onClearAnchorElement}
        onGenerateReport={onReportGenerate}
      />
    </PageBlock>
  );
};

UwReports.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(UwReports);
